import AddToCartButton from 'components/Configurator/ProductsPage/_Components/AddToCartButton'
import GlobalClasses from 'styles/global'
import React from 'react'
import { SRLWrapper } from 'simple-react-lightbox'
import { createUseStyles } from 'react-jss'
import { getCartItemsWithCount } from 'apollo/reactive-variables/configuratorVariables'
import { getImage } from 'helpers/image'
import { useTranslation } from 'react-i18next'

const customBreakpoint = '600px'

const useStyles = createUseStyles((theme) => {
  return {
    ...GlobalClasses,

    cover: {
      zIndex: 1000,
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: '#00000033',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    container: {
      width: '100%',
      maxWidth: '800px',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '4px',
      background: 'white',
      backgroundImage: 'linear-gradient(166deg, #fafafa 0%, #f0f3f8 100%)',
      padding: '30px',
      boxShadow: '1px 2px 30px rgba(0,0,0, .1)',
      margin: 20,
    },

    header: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',

      '&>*:last-child': {
        display: 'flex',
        alignItems: 'center',
      },

      [`@media (max-width: ${customBreakpoint})`]: {
        flexDirection: 'column-reverse',

        '&>*:last-child': {
          width: '100%',
          justifyContent: 'flex-end',
          marginTop: -10,
          marginBottom: 15,
        },
      },
    },

    productLineInfo: {
      display: 'flex',
      justifyContent: 'flex-end',
      width: '170px',

      '& img': {
        height: '24px',
        marginLeft: 6,
      },
      '& span': {
        color: `${theme.colors.darkGrey}`,
        marginRight: '6px',
        '&:first-child': {
          fontWeight: '700',
        },
      },
    },

    separator: {
      background: theme.colors.fadedGrey,
      borderRadius: '1px',
      width: '2px',
      height: '28px',
      margin: '0px 20px',
    },

    closeButton: {
      padding: 0,
      margin: 0,
      fontSize: '20px',
      fontWeight: 700,
    },

    content: {
      paddingRight: 10,
      width: 'calc(100% + 10px)',
      height: '100%',
      display: 'flex',

      maxHeight: 'calc(100vh - 180px)',
      overflowY: 'auto',

      [`@media (max-width: ${customBreakpoint})`]: {
        flexDirection: 'column',
      },
    },

    info: {
      marginBottom: 30,
      marginRight: 30,
      width: 'calc(60% - 30px)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',

      [`@media (max-width: ${customBreakpoint})`]: {
        width: '100%',
      },
    },

    gallery: {
      width: '40%',
      marginTop: 10,
      marginBottom: 5,

      [`@media (max-width: ${customBreakpoint})`]: {
        width: '100%',
      },

      '& button, & a': {
        margin: 0,
        padding: 0,
        border: 'none',
        background: 'none',
        outline: 'none',
        overflow: 'hidden',
      },
      '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: '50% 50%',
      },
    },

    galleryMain: {
      '& button, & a': {
        width: '100%',
        height: '250px',
      },
    },

    galleryThumbnails: {
      '& button': {
        marginTop: 5,
        marginRight: 5,
        width: 'calc(25% - 5px / 4 * 3)',
        height: '70px',

        '&:last-child': {
          marginRight: 0,
        },
      },
      '& img:hover': {
        filter: 'brightness(1.1)',
      },
    },

    galleryNote: {
      marginTop: 5,
      fontSize: '12px',
      lineHeight: '14px',
      color: theme.colors.darkGrey,
    },

    partCode: {
      fontSize: '14px',
      fontWeight: '800',
      marginBottom: 10,
      color: theme.colors.darkGrey,
    },
    partDescShort: {
      fontSize: '16px',
      marginBottom: 10,
      color: theme.colors.darkGrey,
    },
    partDescLong: {
      fontSize: '14px',
      color: theme.colors.darkGrey,
    },

    btnLink: {
      width: '220px',
      fontSize: '12px',
      letterSpacing: 0,
      padding: '5px 15px',
      justifyContent: 'flex-start',
      marginBottom: 14,
      borderRadius: 5,

      '&>i:before': {
        fontSize: '12px',
        top: '6px',
      },
    },
  }
})

type Props = {
  customerSet: any
  productSubLine: any
  part: any
  propName: 'devices' | 'frames'
  handleClose: () => void
  handleAddToCart: () => void
  handleRemoveFromCart: () => void
  manualCartChange?: (newCount: number) => void
}

const PartDetail: React.ComponentType<Props> = (props: Props) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const productLineThumbnail = getImage(
    props.productSubLine?.Thumbnail,
    'thumbnail',
  )

  // GALERIE POD-RADY:
  /* const gallery = props.productSubLine?.Gallery
    ? props.productSubLine.Gallery.slice(0, 4) // only first
    : [] */

  const itemsInCart = getCartItemsWithCount(props.customerSet?.id)[
    props.propName
  ]

  const gallery = props.part?.VisualizerImage
    ? [props.part?.VisualizerImage]
    : []

  const [imageIndex, setImageIndex] = React.useState(0)

  const srlOptions = {
    buttons: {
      showNextButton: false,
      showPrevButton: false,
    },
    thumbnails: {
      showThumbnails: false,
    },
  }

  //
  // RENDER
  //
  return (
    <div className={classes.cover}>
      <div className={classes.container}>
        <div className={classes.header}>
          <h4>{props.part?.Name}</h4>
          <div>
            <div className={classes.productLineInfo}>
              <span>{props.productSubLine?.ProductLine?.Name}</span>
              <span>{props.productSubLine?.Name}</span>
              <img
                src={productLineThumbnail}
                alt={`${props.productSubLine?.ProductLine?.Name} ${props.productSubLine?.Name}`}
              />
            </div>

            <div className={classes.separator} />

            <button
              className={`${classes.textOnlyButton} ${classes.closeButton}`}
              onClick={props.handleClose}
            >
              X
            </button>
          </div>
        </div>
        <div className={classes.content}>
          <div
            className={classes.info}
            style={gallery?.length ? null : { width: '100%', marginBottom: 4 }}
          >
            <section>
              <div className={classes.partCode}>{props.part?.PartNumber}</div>
              <div className={classes.partDescShort}>
                {props.part?.SubType?.Description}
              </div>
              <br />
              <br />
              {/* <p className={classes.partDescLong}>
                  Lorem ipsum, dolor sit amet consectetur adipisicing elit.
                  Veniam minus architecto ut repellendus reiciendis incidunt,
                  nostrum accusantium magni tempore perferendis laborum saepe
                  dolor nulla eum dolorum, sapiente dolore maxime eius.
                </p> */}
            </section>

            <section>
              <a
                className={`${classes.btnSecondary} ${classes.btnWithIcon}-left ${classes.btnLink}`}
                href={`https://www.esklady.cz/vyhledavani/?q=${props.part?.PartNumber}`}
                target="blank"
              >
                <i className="icon-link" />
                {t('configurator:SHOW_ON_ESKLADY')}
              </a>
              <a
                className={`${classes.btnSecondary} ${classes.btnWithIcon}-left ${classes.btnLink}`}
                href={`https://eref.se.com/cz/cs/schneider-electric-cz/product/${props.part?.PartNumber}`}
                target="blank"
              >
                <i className="icon-link" />
                {t('configurator:TECHNICAL_DOC')}
              </a>

              <AddToCartButton
                propName={props.propName}
                part={props.part}
                itemsInCart={itemsInCart}
                brand={props?.productSubLine?.Name}
                handleAddToCart={props.handleAddToCart}
                handleRemoveFromCart={props.handleRemoveFromCart}
                manualCartChange={props.manualCartChange}
                accentStyle={true}
              />
            </section>
          </div>

          {gallery.length ? (
            <div className={classes.gallery}>
              <SRLWrapper options={srlOptions}>
                <div className={classes.galleryMain}>
                  <button>
                    <a
                      href={getImage(gallery[imageIndex], 'large')}
                      data-attribute="SRL"
                    >
                      <img
                        src={getImage(gallery[imageIndex], 'small')}
                        alt={props.productSubLine?.Name}
                      />
                    </a>
                  </button>
                </div>
              </SRLWrapper>
              {gallery?.length > 1 ? (
                <div className={classes.galleryThumbnails}>
                  {gallery.map((imgObj, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setImageIndex(index)
                      }}
                    >
                      <img
                        src={getImage(imgObj, 'thumbnail')}
                        alt={props.productSubLine?.Name}
                      />
                    </button>
                  ))}
                </div>
              ) : null}
              <div className={classes.galleryNote}>
                {t('configurator:IMAGE_ONLY_FOR_REFERENCE')}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default PartDetail
